"use client";

import * as Dialog from "@radix-ui/react-dialog";
import { useTranslations } from "next-intl";
import { useState, type ReactNode } from "react";

import { cn } from "@trip/utils";

import { CloseIcon } from "~/components/icons/close-icon";
import { H3, SunsetCaption } from "~/components/typography";
import { Button, buttonVariants } from "~/components/ui/button";
import { type RouterOutputs } from "~/trpc/types";

import { type TourFromPage } from "../page";

export function AddToCartMobile({
  datoTour,
  trigger,
  inventory,
  children,
  subtitle,
}: {
  children: ReactNode;
  trigger: ReactNode;
  inventory?: Pick<
    RouterOutputs["inventory"]["search"]["results"][0],
    "amount" | "perPerson"
  >;
  subtitle?: ReactNode;
  datoTour: TourFromPage;
}) {
  const [open, setOpen] = useState(false);

  const perPerson = inventory?.perPerson ?? datoTour.perPerson;
  const t = useTranslations("TourDetails");

  return (
    <Dialog.Root
      modal
      onOpenChange={(open) => {
        setOpen(open);
      }}
      open={open}
    >
      <Dialog.Trigger
        className="flex w-full items-center justify-between"
        aria-label={t("addToBag")}
      >
        <div className="flex items-baseline gap-1">
          <div className="text-xl font-semibold sm:text-2xl">{trigger}</div>
          <div className="text-sm font-light text-neutral-500">
            {perPerson ? t("perPerson") : t("perGroup")}
          </div>
        </div>
        <span className={cn(buttonVariants({ variant: "sunset" }), "")}>
          {t("continue")}
        </span>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="pointer-events-none! fixed inset-0 z-20 bg-neutral-200">
          <Dialog.Content className="max-h-screen overflow-y-auto p-5 pb-48">
            <div className="mb-4 flex items-center justify-between">
              <Button
                className="border-prune-500 text-prune-500 border p-1"
                onClick={(event) => {
                  event.preventDefault();
                  setOpen(false);
                }}
                size="circle"
                variant="ghost"
              >
                <CloseIcon />
              </Button>
              <Button
                onClick={(event) => {
                  event.preventDefault();
                }}
                variant="link"
              >
                {t("clearAll")}
              </Button>
            </div>
            <div className="shadow-card-sm mb-4 rounded-2xl bg-white p-3 px-5">
              {datoTour.location ? (
                <SunsetCaption className="text-base">
                  {datoTour.location.name}
                </SunsetCaption>
              ) : null}
              <H3>{datoTour.title}</H3>
              {subtitle}
            </div>
            {children}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
